import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import {
  myCartApi,
  initiateCheckout,
  CheckoutFormContextProvider,
  CheckoutFormStepsEnum,
  CheckoutFormOnSubmitData,
  useUpdateStrings,
  OrderDTO,
  useConfigState,
  LocalLabelsProvider,
  useBulkSubscribe,
  useAllSubscriptions,
  useBoolean,
  getElementOffset,
  useScrollTo,
} from '@sentecacommerce-theme/lib'
import { CheckoutProps, CheckoutLabels } from './checkout.props'
import {
  StepTitle,
  CheckoutShipping,
  Invoice,
  CheckoutPayment,
  OrderSummary,
  CheckoutMenu,
  BasketItemsWithErrorsModal,
} from './Components'
import { useRouter } from 'next/router'
import { FooterButtons } from '../FooterButtons'
import { customBillingAddressValidation } from './utils/validateBillingAddress'

export const Checkout = ({
  labels,
  phone,
  errors,
  footerButtons,
}: CheckoutProps) => {
  useUpdateStrings('checkout', labels)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [gdprAgreed, setGdprAgreed] = useBoolean()
  const [email, setEmail] = useState<string>()
  const config = useConfigState()
  const { data } = myCartApi.useGet()

  const shippingStepRef = useRef(null)
  const paymentStepRef = useRef(null)
  const billingStepRef = useRef(null)

  const { scrollTo: scrollToShipping } = useScrollTo(
    shippingStepRef.current || undefined,
    2
  )

  const { scrollTo: scrollToPayment } = useScrollTo(
    paymentStepRef.current || undefined,
    2
  )

  const { scrollTo: scrollToBilling } = useScrollTo(
    billingStepRef.current || undefined,
    2
  )

  const router = useRouter()

  const { requiredSubs } = useAllSubscriptions({ tag: 'checkout' })
  const [subscribe] = useBulkSubscribe({})

  useEffect(() => {
    // not working on initial load
    data && initiateCheckout(data)
  }, [])

  const onSuccess = ({ _id }: OrderDTO) => {
    router.push(`/order/${_id}?success=true`)
  }

  const onError = (error: any) => {
    console.log('error')
    console.log(error)
  }

  const onSubmit = ({
    isShippingValid,
    setIsShippingSubmitting,
    isBillingValid,
    setIsBillingSubmitting,
    isPaymentValid,
    setIsPaymentSubmitting,
    saveOrder,
  }: CheckoutFormOnSubmitData) => {
    setIsShippingSubmitting(true)
    setIsBillingSubmitting(true)
    setIsPaymentSubmitting(true)

    if (!isShippingValid) {
      scrollToShipping(getElementOffset(shippingStepRef.current).top)
    }

    if (!isPaymentValid) {
      scrollToPayment(getElementOffset(paymentStepRef.current).top)
    }

    if (!isBillingValid) {
      scrollToBilling(getElementOffset(billingStepRef.current).top)
    }

    // if everything is ok send the order
    if (isShippingValid && isBillingValid && isPaymentValid && gdprAgreed) {
      subscribe({ email: email, consentKeys: requiredSubs })
      saveOrder()
    }
  }

  return (
    <LocalLabelsProvider<CheckoutLabels> labels={labels}>
      <CheckoutFormContextProvider
        errorLabels={errors}
        defaultStep={CheckoutFormStepsEnum.All}
        onSubmit={onSubmit}
        countryCode={config.countryCode}
        withPersistBeforePageLeave
        enableSaveAddress
        enableSavedAddressList
        autofillShippingPersonalData
        autoSelectDefaultShippingAddress
        enableSaveBillingAddress
        enableSavedBillingAddressList
        autoSelectDefaultBillingAddress
        shippingEnabledOnStep={CheckoutFormStepsEnum.All}
        billingEnabledOnStep={CheckoutFormStepsEnum.All}
        getPaymentMethodsWhenShippingMethodIsSelected
        onBeforeRedirect={() => setIsRedirecting(true)}
        onSuccess={onSuccess}
        onError={onError}
        validateBillingAddress={customBillingAddressValidation}
      >
        <Background>
          <Container>
            {!isRedirecting && (
              <CheckoutMenu phone={phone}>
                <StepContainer>
                  <StepTitle step="1" title={labels?.deliveryStepTitle} />
                  <StepContent ref={shippingStepRef}>
                    <CheckoutShipping setEmail={setEmail} />
                  </StepContent>
                </StepContainer>
                <StepContainer>
                  <StepTitle step="2" title={labels?.invoiceStepTitle} />
                  <StepContent ref={billingStepRef}>
                    <Invoice />
                  </StepContent>
                </StepContainer>
                <StepContainer>
                  <StepTitle step="3" title={labels?.paymentStepTitle} />
                  <StepContent ref={paymentStepRef}>
                    <CheckoutPayment />
                  </StepContent>
                </StepContainer>
                <StepContainer final>
                  <OrderSummary
                    title={labels?.summaryTitle}
                    gdprAgreed={gdprAgreed}
                    checkGDPR={() => setGdprAgreed.on()}
                    uncheckGDPR={() => setGdprAgreed.off()}
                  />
                </StepContainer>
                <FooterButtonsContaner>
                  <FooterButtons buttons={footerButtons} />
                </FooterButtonsContaner>
              </CheckoutMenu>
            )}
            {isRedirecting && <div>Redirecting...</div>}
          </Container>
        </Background>
        <BasketItemsWithErrorsModal />
      </CheckoutFormContextProvider>
    </LocalLabelsProvider>
  )
}

const Background = styled.div.attrs({
  className: 'py-5',
})`
  background: #fafafa;
`

const StepContent = styled.div.attrs({
  className: 'px-4 py-5',
})`
  margin-top: 2rem;
  background-color: ${({ theme }) => theme.palette.secondary};
  border-radius: 0.4rem;
  margin-bottom: 2rem;
  box-shadow: 0.1rem 0.1rem 0.2rem #bbc6cc;
`

const StepContainer = styled.div<{ final?: boolean }>`
  background-color: ${(props) =>
    props.final ? props.theme.palette.secondary : props.theme.palette.panache};
  padding: ${(props) => (props.final ? '2rem 2rem 4rem' : '2rem 1.5rem 4rem')};
  border-radius: 0.4rem;
  margin-bottom: 2rem;
  box-shadow: ${(props) => (props.final ? '0.1rem 0.1rem 0.2rem #bbc6cc' : 0)};
`

const Container = styled.div.attrs({
  className: 'mx-auto',
})`
  max-width: 120rem;
`
const FooterButtonsContaner = styled.div`
  margin-top: 4rem;
`
